import { makeStyles } from "@material-ui/core/styles";
import { ReactNode } from "react";
import { COPY } from "../constants";
import { rv } from "../tokens";
import { AlertCircleIcon } from "./icons";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  title: {
    fontWeight: 700,
    fontSize: 22,
    letterSpacing: "-0.01em",
    color: rv.heading,
    margin: 0,
    flexGrow: 1,
  },
  stepText: {
    fontSize: 13,
    color: rv.muted,
    fontWeight: 500,
    alignSelf: "center",
  },
  tierChip: {
    alignSelf: "center",
    padding: "4px 12px",
    borderRadius: rv.radiusPill,
    background: rv.goldLight,
    color: rv.goldHover,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  subtitle: {
    margin: theme.spacing(0, 0, 2),
    color: rv.muted,
    fontSize: 14,
  },
  infoBanner: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    background: rv.infoBg,
    border: `1px solid ${rv.infoBorder}`,
    borderRadius: 13,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2.5),
  },
  infoTitle: {
    color: rv.infoTitle,
    fontWeight: 600,
    fontSize: 13,
  },
  infoNote: {
    color: rv.infoSub,
    fontSize: 13,
    lineHeight: 1.5,
  },
  errorBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    background: rv.errorBg,
    border: `1px solid ${rv.errorFg}`,
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2.5),
    color: "#B3261E",
    fontSize: 13,
  },
}));

interface Props {
  title: string;
  subtitle?: string;
  stepText: string;
  tierLabel: string;
  showInfoBanner?: boolean;
  serverError?: string;
  children: ReactNode;
  actionBar: ReactNode;
}

export default function StepShell({
  title,
  subtitle,
  stepText,
  tierLabel,
  showInfoBanner = true,
  serverError,
  children,
  actionBar,
}: Props) {
  const classes = useStyles();
  return (
    <>
      <div className={classes.header}>
        <h1 className={classes.title}>{title}</h1>
        <span className={classes.stepText}>{stepText}</span>
        <span className={classes.tierChip}>{tierLabel}</span>
      </div>
      {subtitle && <p className={classes.subtitle}>{subtitle}</p>}

      {showInfoBanner && (
        <div className={classes.infoBanner}>
          <span className={classes.infoTitle}>{COPY.infoBannerTitle}</span>
          <span className={classes.infoNote}>{COPY.infoBannerNote}</span>
        </div>
      )}

      {serverError && (
        <div className={classes.errorBanner} role="alert">
          <AlertCircleIcon size={18} color="#B3261E" />
          <span>{serverError}</span>
        </div>
      )}

      {children}
      {actionBar}
    </>
  );
}
